import * as React from 'react';
import { observer } from 'mobx-react';

import { Breakpoints } from '@estee/elc-base-theme';
import { configConsumer } from '@estee/elc-service';
import { Mobile } from '@estee/elc-layouts';

import { VtoFoundationShadeGrid } from './VtoFoundationShadeGrid';
import { VtoMakeupShadeGrid } from './VtoMakeupShadeGrid';
import { IVtoShadeGrid } from '~interfaces/IVto';
import { VtoViewController } from '~controllers/VtoViewController';
import { IVtoTranslations } from 'src/exported/vto/Vto';
import { VtoButtonTypes } from '~constants/Vto';

export interface IVtoShadeGridProps {
    shadeGridProps?: IVtoShadeGrid;
    isVtoFoundationEnabled?: boolean;
    isVtoMakeupEnabled?: boolean;
    isVtoShadeGridEnabled: boolean;
    saveMatches(): void;
    removeMatches(): void;
    isMatchSaved: boolean;
    isSaveInProgress: boolean;
    isVtoPriceHidden: boolean;
    config: IVtoShadeGridConfig;
    translations: IVtoTranslations;
    viewController: VtoViewController;
}

interface IVtoShadeGridConfig {
    isVtoFoundationEnabled: boolean;
    isVtoMakeupEnabled: boolean;
}

export const VtoShadeGrids = configConsumer(['isVtoFoundationEnabled', 'isVtoMakeupEnabled'])(
    observer(
        ({
            shadeGridProps,
            isVtoShadeGridEnabled,
            saveMatches,
            removeMatches,
            isMatchSaved,
            isSaveInProgress,
            isVtoPriceHidden,
            config,
            viewController
        }: IVtoShadeGridProps) => {
            if (!isVtoShadeGridEnabled || !shadeGridProps) {
                return null;
            }

            const { isVtoFoundationEnabled, isVtoMakeupEnabled } = config;

            const {
                largeImages,
                selectedAutoReplenishOption,
                productShadePickerProps
            } = shadeGridProps;

            const trackPerfectShadesToggle = (perfectShades: boolean) => {
                const { togglePerfectShades } = shadeGridProps;
                const { handleShadesViewed } = viewController;
                const vtoButtonType = perfectShades
                    ? VtoButtonTypes.BEST_SHADES
                    : VtoButtonTypes.ALL_SHADES;

                togglePerfectShades(perfectShades);
                handleShadesViewed(vtoButtonType);
            };

            if (isVtoFoundationEnabled) {
                const foundationShadeGridProps = {
                    largeImages,
                    selectedAutoReplenishOption,
                    productShadePickerProps,
                    togglePerfectShades: trackPerfectShadesToggle,
                    saveMatches,
                    removeMatches,
                    isMatchSaved,
                    isSaveInProgress,
                    isVtoPriceHidden,
                    viewController
                };

                return <VtoFoundationShadeGrid {...foundationShadeGridProps} />;
            }

            if (isVtoMakeupEnabled) {
                const makeupShadeGridProps = {
                    largeImages,
                    productShadePickerProps,
                    selectedAutoReplenishOption,
                    isVtoPriceHidden
                };

                return (
                    <Mobile maxWidth={Breakpoints.desktop - 1}>
                        {() => <VtoMakeupShadeGrid {...makeupShadeGridProps} />}
                    </Mobile>
                );
            }

            return null;
        }
    )
);
